import digestMessage from '@/domains/common/services/utils/digestMessage';
import useOrderStore from '@/domains/order/store/order.store';
import useSoleStore from '@/domains/sole/store/sole.store';
import SaveAlertHandler from '@/domains/order/services/SaveAlertHandler';
import { computed, watch } from 'vue';
import { storeToRefs } from 'pinia';

export default function useSaveHash() {
    const orderStore = useOrderStore();
    const { currentOrder, saveHash, lastSaveHash } = storeToRefs(orderStore);

    const soleStore = useSoleStore();
    const { soleHash, lastComputeHash, soleCorrectionElementList, soleInformations } = storeToRefs(soleStore);

    // Filling is deliberately absent from soleHash: it changes nothing in the generated
    // 3D model, so it must not trigger a recompute. It still has to be persisted, hence
    // its own contribution to the save hash — otherwise editing only the comblement of an
    // évidement leaves the order clean and the change is silently dropped.
    const fillingTypesAsString = computed<string>(() =>
        JSON.stringify(soleCorrectionElementList.value.map((element) => element.fillingType ?? null)),
    );

    // Same reasoning as the filling: the template fitting is persisted but has no 3D effect
    // of its own. Switching it back to none leaves the zeroed arches untouched, so without
    // this contribution that edit would never mark the order dirty.
    const templateFittingAsString = computed<string>(() => soleInformations.value.templateFitting);

    const orderSettingsAsString = computed<string>(() => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { orderParametersFile, sceneFile, ...orderData } = currentOrder.value;
        const currentOrderString = JSON.stringify(orderData);

        return `${currentOrderString}${soleHash.value}${lastComputeHash.value}${fillingTypesAsString.value}${templateFittingAsString.value}`;
    });

    watch(
        () => orderSettingsAsString.value,
        async () => {
            saveHash.value = await generateHash();
            if (currentOrder.value.id && saveHash.value !== lastSaveHash.value) {
                SaveAlertHandler.instance.start();
            }
        },
    );

    async function generateHash() {
        return digestMessage(orderSettingsAsString.value);
    }

    function updateLastSaveHash() {
        lastSaveHash.value = saveHash.value;
        SaveAlertHandler.instance.stop();
    }

    // Recomputes the hash from the current reactive state and marks the order
    // as saved. Use this when the caller cannot rely on the async watch having
    // flushed (e.g. immediately after a synchronous state change inside the
    // same microtask).
    async function syncLastSaveHash(): Promise<void> {
        saveHash.value = await generateHash();
        updateLastSaveHash();
    }

    const shouldSave = computed(() => {
        return saveHash.value !== lastSaveHash.value;
    });
    return {
        updateLastSaveHash,
        syncLastSaveHash,
        shouldSave,
        saveHash,
        lastSaveHash,
        orderSettingsAsString,
    };
}
